import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { DepartmentColumns } from "../../../SourceData";
import Delete from "./../../../assets/delete.svg";
import Edit from "./../../../assets/edit-user.svg";
import api from "../../../utils/api";
import SwitchButton from "../../../components/SwitchButton/SwitchButton";
import { message } from "antd";
import usePermissions from "../../../hooks/usePermissions";

const MainDepartments = forwardRef(({ query, onEdit, onDelete }, ref) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.length >= 2 || query.length === 0) {
        getData();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [query, page, pageSize]);

  const getData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page + 1, // DataGrid uses 0-based indexing, API expects 1-based
        limit: pageSize, // Changed from per_page to limit to match your API
        type: "factory",
        flag: 1,
      });

      if (query.length >= 2) {
        params.append("search", query);
      }

      const res = await api.get(`/categories?${params.toString()}`);

      // Updated to match your API response structure
      setData(res.data.data);
      setTotalRows(res.data.total || 0); // Use 'total' from your response structure
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("حدث خطأ في تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  // Expose getData method to parent component
  useImperativeHandle(ref, () => ({
    getData,
  }));

  const handleActive = async (row) => {
    setLoading(true);
    try {
      await api.put(`/categories/${row.id}`, {
        active: row.active === true ? false : true,
      });
      getData(); // Refresh current page data
      message.success("تم تعديل الحالة بنجاح");
    } catch (error) {
      console.error("Error updating status:", error);
      message.error("حدث خطأ في تعديل الحالة");
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationModelChange = (model) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  };

  const actionColumn = [
    {
      field: "asdas",
      headerName: "",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: false,
    },
    hasPermission(93) && {
      field: "active",
      headerName: "الحالة",
      minWidth: 80,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.active === true;
        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleActive(params.row)}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "",
      width: 160,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="cell-action">
            {hasPermission(137) && (
              <div className="cell-btn" onClick={() => onEdit(params.row)}>
                <img src={Edit} alt="تعديل" />
              </div>
            )}

            {hasPermission(138) && (
              <div className="cell-btn" onClick={() => onDelete(params.row)}>
                <img src={Delete} alt="حذف" />
              </div>
            )}
          </div>
        );
      },
    },
  ].filter(Boolean); // Remove any falsy values from conditional columns

  return (
    <DataGrid
      sx={{ backgroundColor: "white", width: "100%" }}
      autoHeight={true}
      columns={DepartmentColumns.concat(actionColumn)}
      rows={data.map((row, index) => ({
        ...row,
        index: page * pageSize + index + 1,
      }))}
      disableVirtualization
      disableColumnFilter
      disableColumnMenu
      loading={loading}
      paginationMode="server" // This tells DataGrid that pagination is handled server-side
      rowCount={totalRows}
      pageSizeOptions={[10, 25, 50]}
      paginationModel={{
        page,
        pageSize,
      }}
      onPaginationModelChange={handlePaginationModelChange}
    />
  );
});

export default MainDepartments;
